import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Loader2,
  RefreshCw,
  RotateCcw,
  VolumeX,
  Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  PanelTableCard,
  PanelTableHeader,
  PanelTableFrame,
  PanelTableHeadRow,
  PanelTableHead,
  PanelTableBody,
  PanelTableRow,
  PanelTableCell,
  PanelTableEmpty,
  PanelTableFooter,
  PanelTablePagination,
  TableHeader,
} from "@/components/ui/panel-table";

const MODES = [
  { id: "matched", label: "Eşleşen Kayıtlar", icon: CheckCircle2, accent: "emerald" },
  { id: "unmatched", label: "Eşleşme Bekleyenler", icon: Clock, accent: "amber" },
  { id: "failed", label: "Terk Edilenler", icon: VolumeX, accent: "red" },
];

const formatDateTime = (v) => {
  if (!v || v === "-") return "-";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleString("tr-TR");
};

const StatusBadge = ({ mode, text, isDark }) => {
  if (mode === "matched") {
    return <Badge className={`text-[10px] font-mono ${isDark ? "bg-blue-950 text-blue-300" : "bg-blue-600 text-white"}`}>{text}</Badge>;
  }
  if (mode === "unmatched") {
    return <Badge className={`text-[10px] ${isDark ? "bg-amber-950 text-amber-300 border-amber-700" : "bg-amber-100 text-amber-900 border-amber-300"}`} variant="outline">{text}</Badge>;
  }
  return <Badge className={`text-[10px] ${isDark ? "bg-red-950 text-red-300" : "bg-red-600 text-white"}`}>{text}</Badge>;
};

const SeskPanelSettings = ({ apiBase, isDark, textClass, labelClass }) => {
  const [mode, setMode] = useState("matched");
  const [page, setPage] = useState(1);
  const [limit] = useState(100);
  const [deviceFilter, setDeviceFilter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [payload, setPayload] = useState(null);
  const [selected, setSelected] = useState({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewBulk, setPreviewBulk] = useState(false);

  const inputClass = isDark
    ? "bg-slate-950/80 border-slate-700 text-white"
    : "bg-white border-gray-200 text-gray-900";
  const cardClass = isDark ? "bg-slate-950/50 border-slate-800" : "bg-white border-gray-200 shadow-sm";

  const rows = useMemo(() => payload?.rows || [], [payload?.rows]);
  const total = payload?.total || 0;
  const totalPages = payload?.total_pages || 0;
  const config = payload?.config || {};

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ mode, page: String(page), limit: String(limit) });
      if (mode === "failed" && deviceFilter) params.append("device_id", String(deviceFilter));
      const res = await axios.get(`${apiBase}/sesk-panel?${params}`, { timeout: 120000 });
      setPayload(res.data);
      if (mode === "matched") {
        const init = {};
        (res.data.rows || []).forEach((r) => { init[r.call_id] = true; });
        setSelected(init);
      } else {
        setSelected({});
      }
    } catch (error) {
      console.error("Sesk panel fetch error:", error);
      toast.error("Ses eşleştirme verisi yüklenemedi");
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [apiBase, mode, page, limit, deviceFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => { setPage(1); setDeviceFilter(null); }, [mode]);

  const selectedItems = useMemo(
    () => rows.filter((r) => selected[r.call_id]).map((r) => ({
      call_id: r.call_id,
      original_filename: r.original_filename,
      cagridate: r.cagridate,
    })),
    [rows, selected]
  );

  const runAction = async (action, extra = {}) => {
    setActing(true);
    try {
      const res = await axios.post(`${apiBase}/sesk-panel`, {
        action,
        page,
        limit,
        device_id: deviceFilter,
        ...extra,
      }, { timeout: 120000 });
      toast.success(res.data.message || "İşlem tamamlandı");
      await fetchData();
      return res.data;
    } catch (error) {
      toast.error(error.response?.data?.error || error.response?.data?.detail || "İşlem başarısız");
      return null;
    } finally {
      setActing(false);
    }
  };

  const openPreview = async (items, isBulk) => {
    setActing(true);
    try {
      const res = await axios.post(`${apiBase}/sesk-panel`, {
        action: isBulk ? "preview_bulk" : "preview_single",
        items: isBulk ? items : undefined,
        ...(isBulk ? {} : items[0]),
      });
      if (!res.data.ok) {
        toast.error(res.data.error || "Önizleme alınamadı");
        return;
      }
      setPreviewBulk(isBulk);
      setPreviewData(isBulk ? res.data : res.data.preview);
      setPreviewOpen(true);
    } catch (error) {
      toast.error("Önizleme hatası");
    } finally {
      setActing(false);
    }
  };

  const confirmPreview = async () => {
    if (previewBulk && previewData?.previews) {
      await runAction("update_bulk", { items: previewData.previews.map((p) => ({
        call_id: p.call_id,
        original_filename: p.new_recording_path || p.original_filename,
        cagridate: p.matched_cagridate || p.cagridate,
      })) });
    } else if (previewData) {
      await runAction("update_single", {
        call_id: previewData.call_id,
        original_filename: previewData.new_recording_path,
        cagridate: previewData.matched_cagridate,
      });
    }
    setPreviewOpen(false);
    setPreviewData(null);
  };

  const modeMeta = MODES.find((m) => m.id === mode) || MODES[0];
  const ModeIcon = modeMeta.icon;

  const toolbar = (
    <div className="flex flex-wrap items-center gap-2">
      <Button variant="outline" size="sm" onClick={fetchData} disabled={loading || acting} className={`h-8 ${isDark ? "border-slate-700" : ""}`}>
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
      </Button>
      {mode === "matched" && rows.length > 0 && (
        <Button size="sm" disabled={acting || selectedItems.length === 0} onClick={() => openPreview(selectedItems, true)} className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white">
          <Zap className="w-3.5 h-3.5 mr-1" /> Seçilenleri Güncelle ({selectedItems.length})
        </Button>
      )}
      {mode === "unmatched" && rows.length > 0 && (
        <Button size="sm" variant="destructive" disabled={acting} onClick={() => runAction("update_unmatched_page")} className="h-8">
          Bu Sayfayı Terk Et (kayit=5)
        </Button>
      )}
      {mode === "failed" && rows.length > 0 && (
        <Button size="sm" disabled={acting} onClick={() => runAction("reset_failed_page")} className={`h-8 ${isDark ? "bg-cyan-700 hover:bg-cyan-600" : "bg-cyan-600 hover:bg-cyan-700"} text-white`}>
          <RotateCcw className="w-3.5 h-3.5 mr-1" /> Sayfayı Yeniden Ara
        </Button>
      )}
      <Button size="sm" variant="outline" disabled={acting} onClick={() => runAction("run_matched_batch", { batch_limit: 100 })} className={`h-8 text-xs ${isDark ? "border-slate-700" : ""}`}>
        Toplu Eşleştir (100)
      </Button>
      <Button size="sm" variant="outline" disabled={acting} onClick={() => runAction("pool_reset")} className={`h-8 text-xs ${isDark ? "border-slate-700" : ""}`}>
        Havuz Reset
      </Button>
      <Button size="sm" variant="outline" disabled={acting} onClick={() => runAction("refresh_stats")} className={`h-8 text-xs ${isDark ? "border-slate-700" : ""}`}>
        İstatistik Yenile
      </Button>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className={`inline-flex flex-wrap gap-1 p-1 rounded-xl ${isDark ? "bg-slate-900/80 border border-slate-800" : "bg-gray-100 border border-gray-200"}`}>
        {MODES.map((m) => {
          const Icon = m.icon;
          const active = mode === m.id;
          return (
            <button
              key={m.id}
              type="button"
              onClick={() => setMode(m.id)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                active
                  ? m.id === "failed"
                    ? "bg-red-600 text-white shadow-sm"
                    : m.id === "unmatched"
                      ? (isDark ? "bg-amber-600 text-white shadow-sm" : "bg-amber-500 text-white shadow-sm")
                      : (isDark ? "bg-emerald-600 text-white shadow-sm" : "bg-emerald-600 text-white shadow-sm")
                  : isDark ? "text-slate-400 hover:text-slate-200 hover:bg-slate-800" : "text-gray-600 hover:text-gray-900 hover:bg-white/70"
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {m.label}
            </button>
          );
        })}
      </div>

      {mode === "failed" && payload?.failed_device_summary?.length > 0 && (
        <div className={`rounded-xl border p-3 ${isDark ? "border-red-900/40 bg-red-950/20" : "border-red-100 bg-red-50/50"}`}>
          <p className={`text-xs mb-2 ${labelClass}`}>
            Cihaz bazında terk edilen kayıtlar · Toplam {payload.failed_total_all?.toLocaleString("tr-TR")} kayıt
            {deviceFilter && (
              <button type="button" onClick={() => { setDeviceFilter(null); setPage(1); }} className={`ml-2 underline ${isDark ? "text-cyan-400" : "text-cyan-600"}`}>
                Filtreyi temizle (#{deviceFilter})
              </button>
            )}
          </p>
          <div className="flex flex-wrap gap-2 max-h-28 overflow-y-auto">
            {payload.failed_device_summary.map((d) => {
              const blocked = !d.havuza_alinabilir;
              const active = deviceFilter === d.device_id;
              return (
                <button
                  key={d.device_id}
                  type="button"
                  onClick={() => { setDeviceFilter(d.device_id); setPage(1); }}
                  className={`rounded-lg border px-2.5 py-1.5 text-left min-w-[88px] transition-all ${
                    active
                      ? (isDark ? "border-red-400 bg-red-950/50 ring-1 ring-red-500/40" : "border-red-400 bg-white ring-1 ring-red-300")
                      : (isDark ? "border-slate-700 bg-slate-900/50 hover:border-red-700" : "border-gray-200 bg-white hover:border-red-200")
                  } ${blocked ? "opacity-75" : ""}`}
                >
                  <p className={`text-sm font-bold font-mono ${isDark ? "text-red-300" : "text-red-600"}`}>{d.adet ?? d.terk_adet}</p>
                  <p className={`text-[10px] ${labelClass}`}>ID {d.device_id}</p>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <PanelTableCard isDark={isDark}>
        <PanelTableHeader
          isDark={isDark}
          textClass={textClass}
          labelClass={labelClass}
          accent={modeMeta.accent}
          icon={ModeIcon}
          title={modeMeta.label}
          description={`kayit=${mode === "failed" ? config.kayit_terk : config.kayit_tarama} · ${total.toLocaleString("tr-TR")} kayıt · sayfa ${page}/${totalPages || 1}`}
          toolbar={toolbar}
        />
        <PanelTableFrame
          isDark={isDark}
          footer={totalPages > 1 ? (
            <PanelTableFooter isDark={isDark}>
              <PanelTablePagination
                isDark={isDark}
                page={page}
                pageSize={limit}
                total={total}
                totalPages={totalPages}
                onPageChange={setPage}
                loading={loading}
              />
            </PanelTableFooter>
          ) : null}
        >
          <TableHeader>
            <PanelTableHeadRow isDark={isDark}>
              {mode === "matched" && <PanelTableHead isDark={isDark} className="w-10" />}
              <PanelTableHead isDark={isDark}>Call ID</PanelTableHead>
              <PanelTableHead isDark={isDark}>Device</PanelTableHead>
              <PanelTableHead isDark={isDark}>Telefon</PanelTableHead>
              <PanelTableHead isDark={isDark}>Kişi</PanelTableHead>
              <PanelTableHead isDark={isDark}>Çağrı Tarihi</PanelTableHead>
              <PanelTableHead isDark={isDark} align="center">Tarama</PanelTableHead>
              <PanelTableHead isDark={isDark}>Durum / Dosya</PanelTableHead>
              <PanelTableHead isDark={isDark}>Ses Tarihi</PanelTableHead>
              <PanelTableHead isDark={isDark} align="right">Fark / Onay</PanelTableHead>
            </PanelTableHeadRow>
          </TableHeader>
          <PanelTableBody>
            {loading ? (
              <PanelTableRow isDark={isDark} className="hover:bg-transparent">
                <PanelTableCell isDark={isDark} colSpan={mode === "matched" ? 10 : 9} className="py-16 text-center">
                  <Loader2 className={`w-6 h-6 animate-spin inline mr-2 ${isDark ? "text-slate-400" : "text-gray-400"}`} />
                  <span className={labelClass}>Yükleniyor…</span>
                </PanelTableCell>
              </PanelTableRow>
            ) : rows.length === 0 ? (
              <PanelTableEmpty colSpan={mode === "matched" ? 10 : 9} isDark={isDark} title="Kayıt yok" description="Bu grupta işlem yapılacak kayıt bulunmuyor." />
            ) : rows.map((row) => (
              <PanelTableRow key={row.call_id} isDark={isDark}>
                {mode === "matched" && (
                  <PanelTableCell isDark={isDark} className="w-10">
                    <Checkbox
                      checked={!!selected[row.call_id]}
                      onCheckedChange={(c) => setSelected((prev) => ({ ...prev, [row.call_id]: c === true }))}
                    />
                  </PanelTableCell>
                )}
                <PanelTableCell isDark={isDark} mono>{row.call_id}</PanelTableCell>
                <PanelTableCell isDark={isDark} mono>{row.device_id}</PanelTableCell>
                <PanelTableCell isDark={isDark} mono>{row.phone_number || "-"}</PanelTableCell>
                <PanelTableCell isDark={isDark}>{row.contact_name || "-"}</PanelTableCell>
                <PanelTableCell isDark={isDark} className="whitespace-nowrap text-xs">{formatDateTime(row.call_date2)}</PanelTableCell>
                <PanelTableCell isDark={isDark} align="center">
                  <Badge variant="outline" className={`font-mono text-[10px] ${isDark ? "border-slate-600" : ""}`}>
                    {row.tarama_sayisi ?? 0}/{config.tarama_max ?? 3}
                  </Badge>
                </PanelTableCell>
                <PanelTableCell isDark={isDark}>
                  <StatusBadge mode={mode} text={row.original_filename} isDark={isDark} />
                </PanelTableCell>
                <PanelTableCell isDark={isDark} className="text-xs whitespace-nowrap">{row.cagridate === "-" ? "-" : formatDateTime(row.cagridate)}</PanelTableCell>
                <PanelTableCell isDark={isDark} align="right">
                  {row.saniye_farki !== "-" && row.saniye_farki != null ? (
                    <Badge variant="outline" className={`font-mono text-[10px] ${isDark ? "border-cyan-800 text-cyan-400" : "border-cyan-200 text-cyan-700"}`}>+{row.saniye_farki} sn</Badge>
                  ) : mode === "matched" ? (
                    <Button size="sm" variant="outline" className="h-7 text-xs" disabled={acting} onClick={() => openPreview([{
                      call_id: row.call_id,
                      original_filename: row.original_filename,
                      cagridate: row.cagridate,
                    }], false)}>
                      Onay
                    </Button>
                  ) : "-"}
                </PanelTableCell>
              </PanelTableRow>
            ))}
          </PanelTableBody>
        </PanelTableFrame>
      </PanelTableCard>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className={`max-w-lg ${isDark ? "bg-slate-900 border-slate-800 text-white" : ""}`}>
          <DialogHeader>
            <DialogTitle>Güncelleme Önizlemesi</DialogTitle>
          </DialogHeader>
          <div className={`text-xs space-y-2 max-h-[50vh] overflow-y-auto ${labelClass}`}>
            {previewBulk && previewData?.previews ? (
              previewData.previews.map((p, i) => (
                <div key={p.call_id} className={`p-2 rounded border ${isDark ? "border-slate-700 bg-slate-800/50" : "border-gray-200 bg-gray-50"}`}>
                  <p className={`font-semibold ${textClass}`}>#{i + 1} · Call {p.call_id}</p>
                  <p>{p.phone_number || "-"} / {p.contact_name || "-"}</p>
                  <p className="font-mono mt-1">{p.new_recording_path}</p>
                </div>
              ))
            ) : previewData ? (
              <>
                <p><strong>Call ID:</strong> {previewData.call_id}</p>
                <p><strong>Device:</strong> {previewData.device_id}</p>
                <p><strong>Dosya:</strong> <span className="font-mono">{previewData.new_recording_path}</span></p>
                <p><strong>Zaman farkı:</strong> +{previewData.saniye_farki} sn</p>
                <p><strong>kayit:</strong> {previewData.current_kayit} → 1</p>
              </>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPreviewOpen(false)}>Vazgeç</Button>
            <Button onClick={confirmPreview} disabled={acting} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Güncelle"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SeskPanelSettings;
